import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { LoggerService } from './logging/logger.service';
import { NbToastrService } from '@nebular/theme';
import { Howl } from 'howler'
import { getCurrencySymbol } from '@angular/common';

export interface TapeEnvironmentalData {
  degaussedShreddedIncineratedCO2: number,
  driveCO2: number,
  degaussCO2: number,
  reuseCO2: number,
  incinerateCO2: number,
  recycleCO2: number
}
export interface MetaData {
  spokeData: {
    name: string; // i.e. Insurgo Media
    ceoString: string; // i.e. Gavin Griffiths - Managing Director
    address: string;      
    email: string;
    website: string;
  };
  environmentalData: {
    lto:TapeEnvironmentalData,
    jag:TapeEnvironmentalData
  };
}

export interface Configuration {
  default: boolean;
  auditAllowed: boolean;
  sendAllAllowed: boolean;
  manifestAllowed: boolean;
  isDrivesUsageTableBeingUsed: boolean;
  isGenerateCertificatesEnabled: boolean;
  isAllowKitResetEnabled: boolean;
  isFinishTapeEnabled: boolean;
  defaultCurrency: string;
  metaData: MetaData;
  isR2v3ReportingEnabled: boolean;
  isDemoEnvironment: boolean;
}

interface ConfigurationDatabase {
  auditEnabled: boolean;
  sendAllTapeEnabled: boolean;
  manifestEnabled: boolean;
  drivesUsageTableEnabled: boolean;
  generateCertificatesEnabled: boolean;
  allowKitReset: boolean;
  finishTapeEnabled: boolean;
  defaultCurrency: string;
  r2v3ReportingEnabled: boolean;
  metaData: MetaData;
  isDemoEnvironment: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ConfigurationService {

  apiUrl = environment.apiUrl;
  siteConfiguration: Configuration;

  constructor(
    public authHttp: HttpClient,
    private readonly logger: LoggerService,
    private toastrService: NbToastrService
  ) { }

  public successMessageTimeOut = 1500;
  public successSnd = new Howl({
    src: ['../../assets/sound/success.wav']
  });
  public errorSnd = new Howl({
    src: ['../../assets/sound/alert.wav']
  });

  public getCurrencySymbol(): string {
    const currency = this.getConfigurationSettings().defaultCurrency;
    return getCurrencySymbol(currency, 'narrow') || '';
  }

  getConfigurationSettings(): Configuration {
    if (this.siteConfiguration) {
      return this.siteConfiguration;
    }
    throw new Error('site not configured');
  }

  setConfigurationSettings() {
    return this.getDatabaseModel().toPromise().then(async config => {
      // await new Promise(resolve => setTimeout(resolve, 5000));
      this.siteConfiguration = {
        default: false,
        auditAllowed: config.auditEnabled,
        sendAllAllowed: config.sendAllTapeEnabled,
        manifestAllowed: config.manifestEnabled,
        isDrivesUsageTableBeingUsed: config.drivesUsageTableEnabled,
        isGenerateCertificatesEnabled: config.generateCertificatesEnabled,
        isAllowKitResetEnabled: config.allowKitReset,
        isFinishTapeEnabled: config.finishTapeEnabled,
        defaultCurrency: config.defaultCurrency,
        metaData: config.metaData,
        isR2v3ReportingEnabled: config.r2v3ReportingEnabled,
        isDemoEnvironment: config.isDemoEnvironment === true,
      }
      console.log('Configuration loaded');
    }, async error => {
      this.siteConfiguration = {
        default: true,
        auditAllowed: false,
        sendAllAllowed: false,
        manifestAllowed: true,
        isDrivesUsageTableBeingUsed: false,
        isGenerateCertificatesEnabled: false,
        isAllowKitResetEnabled: false,
        isFinishTapeEnabled: false,
        defaultCurrency: 'GBP',
        isR2v3ReportingEnabled: false,
        isDemoEnvironment: false,
        metaData: {
          spokeData: {
            name: 'Investigo',
            ceoString: 'Investigo',
            address: '',
            email: '',
            website: ''
          },
          environmentalData: {
            lto:{
              degaussedShreddedIncineratedCO2: 0.648,
              driveCO2: 0.000114,
              degaussCO2: 0.000314,
              reuseCO2: 0.0192,
              incinerateCO2: 0.536,
              recycleCO2: 0.305,
            }, jag:{
              degaussedShreddedIncineratedCO2: 0.678,
              driveCO2: 0.000114,
              degaussCO2: 0.000314,
              reuseCO2: 0.0192,
              incinerateCO2: 0.566,
              recycleCO2: 0.350,
            }
            
          }
        }
      };
      console.log('Unable to configure site, use defaults...');
    });
  }

  playErrorSound(times: number, delay: number): void {
    if (times <= 0) {
      return;
    }
    this.errorSnd.play();
    setTimeout(() => {
      this.playErrorSound(times - 1, delay);
    }, delay);
  }

  private getDatabaseModel(): Observable<ConfigurationDatabase> {
    const URL = `${this.apiUrl}/configuration`;

    return this.authHttp.get<ConfigurationDatabase>(URL)
      .pipe(
        catchError(this.handleError('getDatabaseModel', URL))
      );
  }

  private handleError(operation: String, url: String): any {
    return (err: any) => {
      const errMsg = `error in ${operation}() retrieving ${url}`;
      this.logger.error(`${errMsg}:`, err);
      if (err instanceof HttpErrorResponse) {
        // you could extract more info about the error if you want, e.g.:
        // console.log(`status: ${err.status}, ${err.statusText}`);
      }
      return throwError(() => new Error(errMsg));
    };
  }
}
